// GeoJSON coordinates are [lng, lat]; google.maps paths are {lat, lng}.
// GeoJSON rings are closed, but Google Maps editable polygons should not render
// the duplicated closing vertex.
export const toGoogleMapsPath = (boundary) => {
  const path = (boundary?.coordinates?.[0] || []).map(([lng, lat]) => ({
    lat,
    lng,
  }));
  const first = path[0];
  const last = path[path.length - 1];

  if (first && last && first.lat === last.lat && first.lng === last.lng) {
    return path.slice(0, -1);
  }

  return path;
};

// Closes the ring (first point repeated at the end) per GeoJSON Polygon spec.
export const pathToGeoJson = (path) => {
  const ring = path.map(({ lat, lng }) => [lng, lat]);
  if (
    ring.length &&
    (ring[0][0] !== ring[ring.length - 1][0] ||
      ring[0][1] !== ring[ring.length - 1][1])
  ) {
    ring.push(ring[0]);
  }
  return { type: "Polygon", coordinates: [ring] };
};

// Simple arithmetic-mean centroid — sufficient for small neighborhood-sized
// polygons; deliberately not dependent on the `geometry` library so this
// module has zero Google-Maps-runtime coupling and stays unit-testable.
export const computeCentroid = (path) => {
  const n = path.length || 1;
  const sum = path.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
    { lat: 0, lng: 0 },
  );
  return { lat: sum.lat / n, lng: sum.lng / n };
};

// ~metersPerSide box centered on {lat,lng}, used as the default suggested
// boundary in the search-assisted flow when the geocode result has no viewport.
export const boxAround = (lat, lng, metersPerSide = 250) => {
  const half = metersPerSide / 2;
  const dLat = half / 111320; // meters per degree latitude, ~constant
  const dLng = half / (111320 * Math.cos((lat * Math.PI) / 180));
  return [
    { lat: lat - dLat, lng: lng - dLng },
    { lat: lat - dLat, lng: lng + dLng },
    { lat: lat + dLat, lng: lng + dLng },
    { lat: lat + dLat, lng: lng - dLng },
  ];
};

// Extracts a suggested rectangle from a Google geocode result's viewport
// (a google.maps.LatLngBounds), when one is available.
export const boxFromViewport = (viewport) => {
  if (!viewport) return null;
  const ne = viewport.getNorthEast();
  const sw = viewport.getSouthWest();
  return [
    { lat: sw.lat(), lng: sw.lng() },
    { lat: sw.lat(), lng: ne.lng() },
    { lat: ne.lat(), lng: ne.lng() },
    { lat: ne.lat(), lng: sw.lng() },
  ];
};

export const areaPoint = (area = {}) => {
  const lat = Number.parseFloat(area?.latitude ?? area?.centroid?.lat);
  const lng = Number.parseFloat(area?.longitude ?? area?.centroid?.lng);

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

export const areaMapPath = (area = {}, fallbackMetersPerSide = 300) => {
  const boundaryPath = toGoogleMapsPath(area.boundary);
  if (boundaryPath.length) return boundaryPath;

  const point = areaPoint(area);
  return point ? boxAround(point.lat, point.lng, fallbackMetersPerSide) : [];
};

// Monotone-chain convex hull over {lat,lng} points (lng as x, lat as y).
// Returns the input as-is when there are fewer than 3 unique points.
export const convexHull = (points) => {
  const unique = points.filter(
    (p, i, arr) =>
      arr.findIndex((q) => q.lat === p.lat && q.lng === p.lng) === i,
  );
  if (unique.length < 3) return unique;

  const sorted = [...unique].sort(
    (a, b) => a.lng - b.lng || a.lat - b.lat,
  );
  const cross = (o, a, b) =>
    (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);

  const lower = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }

  lower.pop();
  upper.pop();
  return [...lower, ...upper];
};

// Approximates the area a zone covers from its areas' real coordinates
// (the backend only stores a single {latitude,longitude} per area, not a
// drawn boundary), so this is a best-effort convex hull rather than an
// exact shape. Falls back to a small padded box when there aren't enough
// points to form a hull, so every zone still renders something.
export const zoneAreaPolygon = (areas = []) => {
  const points = areas.flatMap((area) => {
    const boundaryPoints = (area?.boundary?.coordinates?.[0] || []).map(
      ([lng, lat]) => ({ lat, lng }),
    );
    if (boundaryPoints.length) return boundaryPoints;

    const lat = parseFloat(area?.latitude ?? area?.centroid?.lat);
    const lng = parseFloat(area?.longitude ?? area?.centroid?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng)
      ? [{ lat, lng }]
      : [];
  });

  if (!points.length) return [];

  const hull = convexHull(points);
  if (hull.length < 3) {
    const centroid = computeCentroid(points);
    return boxAround(centroid.lat, centroid.lng, 500);
  }

  // A zone's named areas often sit along a single corridor/road, so their
  // convex hull can be a near-collinear sliver — scaling it outward by a
  // percentage of its own (near-zero) width would still be invisible.
  // Push every vertex out from the hull's centroid by an absolute minimum
  // distance (plus a size-proportional amount for larger hulls) so every
  // zone renders as a clearly visible shape rather than a thin line.
  const hullCentroid = computeCentroid(hull);
  const MIN_BUFFER_METERS = 350;
  return hull.map((p) => {
    const dLat = p.lat - hullCentroid.lat;
    const dLng = p.lng - hullCentroid.lng;
    const dist = Math.hypot(dLat, dLng) || 1e-9;
    const minBufferDeg = MIN_BUFFER_METERS / 111320;
    const bufferDeg = Math.max(dist * 0.35, minBufferDeg);
    const scale = (dist + bufferDeg) / dist;
    return {
      lat: hullCentroid.lat + dLat * scale,
      lng: hullCentroid.lng + dLng * scale,
    };
  });
};
